/**
 * Florame CAP & Stock System - INLINE MODULE v31.0
 * Based on UNIFIED CAP LOGIC provided.
 * Features: Session Tokens, Robust Autocomplete, Backend Validation.
 */
(function () {
    const CONFIG = {
        stockEndpoint: "https://us-central1-flower-ai-2d7aa.cloudfunctions.net/getStockByCap",
        addressAutocomplete: "https://us-central1-flower-ai-2d7aa.cloudfunctions.net/addressAutocomplete",
        addressResolve: "https://us-central1-flower-ai-2d7aa.cloudfunctions.net/addressResolve",
        partnerDirectoryEndpoint: "https://us-central1-flower-ai-2d7aa.cloudfunctions.net/portalPublicPartnerDirectoryV1",
        coverageWaitlistEndpoint: "https://us-central1-flower-ai-2d7aa.cloudfunctions.net/collectCoverageWaitlist",
        privacyUrl: "/policies/privacy-policy",
        capValidityMs: 2 * 60 * 60 * 1000 // 2 hours
    };
    
    const STORAGE_KEY = 'florame_cap_state';
    const ADDR_CACHE_KEY = 'florame_last_address';
    const $ = (id) => document.getElementById(id);

    function __partnerSlug(value) {
        return String(value || '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    function __requestedPartnerSlug() {
        try {
            const params = new URLSearchParams(window.location.search || '');
            const explicit = __partnerSlug(params.get('partner') || params.get('partnerSlug') || '');
            if (explicit) return explicit;
        } catch (_) {}
        try {
            const contextual = __partnerSlug(window.FloramePartner?.getAttributionMeta?.()?.partnerSlug || '');
            if (contextual) return contextual;
        } catch (_) {}
        try {
            const partner = window.__floramePartner || {};
            const resolved = __partnerSlug(partner.portal?.partnerSlug || partner.partnerSlug || partner.slug || partner.partnerId || '');
            if (resolved) return resolved;
        } catch (_) {}
        try {
            return __partnerSlug(JSON.parse(sessionStorage.getItem('florame_partner_context_v2') || 'null')?.slug || '');
        } catch (_) {
            return '';
        }
    }

    function __stockEndpointUrl(cap, meta = {}) {
        const url = new URL(CONFIG.stockEndpoint, window.location.origin);
        url.searchParams.set('cap', String(cap || ''));
        const lat = Number(meta.lat);
        const lng = Number(meta.lng);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
            url.searchParams.set('lat', String(lat));
            url.searchParams.set('lng', String(lng));
        }
        const requestedPartner = __requestedPartnerSlug();
        if (requestedPartner) url.searchParams.set('partnerSlug', requestedPartner);
        return url.toString();
    }

    function __partnerAssignmentFromResponse(data) {
        const requestedPartner = __requestedPartnerSlug();
        const partner = data && data.partner && typeof data.partner === 'object' ? data.partner : null;
        const matchedPartner = __partnerSlug(partner?.partnerSlug || partner?.partnerId || '');
        const assignmentSource = String(partner?.source || (requestedPartner && matchedPartner === requestedPartner ? 'partner_slug' : '')).trim();
        const currentPartner = window.__floramePartner || {};
        const requestedAliases = new Set([
            requestedPartner,
            __partnerSlug(currentPartner.portal?.partnerSlug || ''),
            __partnerSlug(currentPartner.partnerSlug || ''),
            __partnerSlug(currentPartner.slug || ''),
            __partnerSlug(currentPartner.partnerId || ''),
            __partnerSlug(currentPartner.id || '')
        ].filter(Boolean));
        const matchedAliases = [
            matchedPartner,
            __partnerSlug(partner?.partnerId || ''),
            __partnerSlug(partner?.id || '')
        ].filter(Boolean);
        const isRequestedPartner = matchedAliases.some((alias) => requestedAliases.has(alias));
        const partnerCoverage = !requestedPartner
            ? (data?.covered !== false)
            : !!(
                data?.covered !== false
                && (assignmentSource === 'partner_slug' || isRequestedPartner)
                && assignmentSource !== 'cap_fallback'
            );
        return {
            requestedPartner: requestedPartner || '',
            requestedPartnerSlug: requestedPartner || '',
            matchedPartner: matchedPartner || '',
            matchedPartnerSlug: matchedPartner || '',
            matchedPartnerName: String(partner?.name || '').trim(),
            assignedPartnerSlug: partnerCoverage ? (matchedPartner || '') : '',
            assignmentSource,
            partnerCoverage,
            selectedPartnerCovered: partnerCoverage,
            coverageState: requestedPartner
                ? (partnerCoverage ? 'covered' : 'uncovered')
                : 'covered',
            globalCovered: data?.covered !== false
        };
    }

    // ───────────────────────────────────────────────────────────────
    // 📍 address_attempts logging (parità con AWI/Home Init)
    // Cloud Function: logAddressAttempt -> Firestore collection: address_attempts
    // ───────────────────────────────────────────────────────────────
    const LOG_ATTEMPT_ENDPOINT = "https://us-central1-flower-ai-2d7aa.cloudfunctions.net/logAddressAttempt";
    const SESSION_ID_KEY = 'florame_cap_session_id';

    function __getSessionId() {
        try {
            const existing = localStorage.getItem(SESSION_ID_KEY);
            if (existing) return existing;
            const sid = (window.crypto && typeof window.crypto.randomUUID === 'function')
                ? window.crypto.randomUUID()
                : ('sid_' + Math.random().toString(16).slice(2) + Date.now().toString(16));
            localStorage.setItem(SESSION_ID_KEY, sid);
            return sid;
        } catch (_) {
            return 'sid_' + Math.random().toString(16).slice(2);
        }
    }

    function __newAddressAttemptId(prefix) {
        try {
            const id = (window.crypto && typeof window.crypto.randomUUID === 'function')
                ? window.crypto.randomUUID()
                : (Date.now().toString(36) + '_' + Math.random().toString(36).slice(2));
            return String(prefix || 'address_attempt') + '_' + id;
        } catch (_) {
            return String(prefix || 'address_attempt') + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2);
        }
    }

    function __addressAttemptPageMeta() {
        try {
            let tz = '';
            try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (_) {}
            return {
                page: String((window.location && window.location.href) || '').slice(0, 1000),
                referrer: String(document.referrer || '').slice(0, 1000),
                locale: String((document.documentElement && document.documentElement.lang) || navigator.language || '').slice(0, 32),
                tz: String(tz || '').slice(0, 64)
            };
        } catch (_) { return {}; }
    }

    function __normalizeAttemptPayload(p) {
        try {
            const payload = p || {};
            const status = String(payload.status || '').trim();
            const q = String(payload.query ?? payload.input ?? payload.display ?? payload.resolvedDisplay ?? '').trim();
            const d = String(payload.display ?? payload.resolvedDisplay ?? payload.query ?? payload.input ?? '').trim();

            let isAddr = payload.isAddress;
            if (typeof isAddr !== 'boolean') {
                if (status === 'success' || status === 'not_served' || status === 'start') isAddr = true;
                else if (status === 'invalid_address' || status === 'missing_selection' || status === 'resolve_error') isAddr = false;
            }

            const out = { ...(payload || {}) };
            if (!out.query && q) out.query = q;
            if (!out.display && d) out.display = d;
            if (typeof out.isAddress !== 'boolean' && typeof isAddr === 'boolean') out.isAddress = isAddr;
            return out;
        } catch (_) { return p || {}; }
    }

    function __logAddressAttempt(payload) {
        try {
            const customerEmail = String(
                (payload && (payload.customerEmail || payload.email)) ||
                (() => {
                    try {
                        return window.AWI_CUSTOMER_EMAIL || window.__AWI_CUSTOMER_EMAIL || window.customerEmail || localStorage.getItem('florame_customer_email') || '';
                    } catch (_) { return ''; }
                })() ||
                ''
            ).trim().toLowerCase();
            const body = { ...__addressAttemptPageMeta(), ...__normalizeAttemptPayload(payload), sessionId: __getSessionId(), customerEmail };
            fetch(LOG_ATTEMPT_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
                keepalive: true
            }).catch(() => {});
        } catch (_) {}
    }

    // --- HELPERS (Ported from Unified Logic) ---

    function __uuidv4() {
        try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (_) {}
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    }

    function ensureSessionToken(input) {
        if (!input.dataset.sessionToken) input.dataset.sessionToken = __uuidv4();
        return input.dataset.sessionToken;
    }

    function resetSessionToken(input) { delete input.dataset.sessionToken; }

    function __addressComponentValue(components, wantedType) {
        const list = Array.isArray(components) ? components : [];
        for (const component of list) {
            const types = Array.isArray(component?.types) ? component.types : [component?.type].filter(Boolean);
            if (!types.includes(wantedType)) continue;
            return String(component?.longText || component?.longName || component?.text || component?.shortText || component?.shortName || '').trim();
        }
        return '';
    }

    function __canonicalItalianAddress(raw, components) {
        const route = __addressComponentValue(components, 'route');
        const streetNumber = __addressComponentValue(components, 'street_number');
        const postcode = __addressComponentValue(components, 'postal_code');
        const city = __addressComponentValue(components, 'locality') || __addressComponentValue(components, 'postal_town');
        const province = __addressComponentValue(components, 'administrative_area_level_2') || __addressComponentValue(components, 'administrative_area_level_3');
        if (route && streetNumber) {
            const locality = [postcode, city, province].filter(Boolean).join(' ');
            return [`${route} ${streetNumber}`, locality].filter(Boolean).join(', ');
        }

        const parts = String(raw || '').split(',').map((part) => part.trim()).filter(Boolean);
        if (!parts.length) return '';
        const civicPattern = '(\\d+[A-Za-z]?(?:\\s*(?:\\/|-)\\s*[A-Za-z0-9]+)?(?:\\s*(?:bis|ter|quater))?)';
        const numberFirst = parts[0].match(new RegExp(`^${civicPattern}\\s+(.+)$`, 'i'));
        if (numberFirst && /[A-Za-zÀ-ÿ]/.test(numberFirst[2] || '')) parts[0] = `${numberFirst[2].trim()} ${numberFirst[1].trim()}`;
        if (parts.length > 1 && new RegExp(`^${civicPattern}$`, 'i').test(parts[1]) && /[A-Za-zÀ-ÿ]/.test(parts[0])) {
            parts[0] = `${parts[0]} ${parts[1]}`;
            parts.splice(1, 1);
        }
        return parts.filter((part) => !/^italia$/i.test(part)).join(', ');
    }

    function __formatAddrSuggestion(s) {
        const text = __canonicalItalianAddress(s.text || s.display || s.label || '', s.addressComponents || s.components || []);
        const main = s.mainText || (s.structuredFormat && s.structuredFormat.mainText && s.structuredFormat.mainText.text) || '';
        const secondary = s.secondaryText || (s.structuredFormat && s.structuredFormat.secondaryText && s.structuredFormat.secondaryText.text) || '';
        const pcHint = s.postcodeHint || s.postcode || s.postalCode || '';
        
        const mainShort = (main || (text.split(',')[0] || text)).trim();
        const subNoCap = String(secondary || '').trim();
        const sub = [pcHint, subNoCap].filter(Boolean).join(' · ');

        return {
            ...s,
            display: text || [mainShort, subNoCap].filter(Boolean).join(', '),
            __mainShort: mainShort || text,
            __sub: sub,
            __subNoCap: subNoCap,
            __postcode: pcHint || ''
        };
    }


    // ───────────────────────────────────────────────────────────────
    // Address Book (Recenti + Salvati) — shared with Florame App
    // Storage: localStorage['florame_address_book_v1']
    // ───────────────────────────────────────────────────────────────
    const __ADDR_BOOK_KEY = 'florame_address_book_v1';
    const __ADDR_BOOK_MAX_RECENT = 10;
    const __ADDR_BOOK_MAX_SAVED = 20;

    function __addrBookSafeParse(raw) {
        try { return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
    }
    function __addrBookLoad() {
        const base = { v: 1, recent: [], saved: [] };
        try {
            const data = __addrBookSafeParse(localStorage.getItem(__ADDR_BOOK_KEY));
            if (!data || typeof data !== 'object') return base;
            return {
                v: 1,
                recent: Array.isArray(data.recent) ? data.recent : [],
                saved: Array.isArray(data.saved) ? data.saved : []
            };
        } catch (_) { return base; }
    }
    function __addrBookSave(book) {
        try { localStorage.setItem(__ADDR_BOOK_KEY, JSON.stringify(book)); } catch (_) {}
    }
    function __addrBookNormalize(entry) {
        const placeId = String(entry?.placeId || entry?.place_id || '').trim();
        if (!placeId) return null;
        const display = String(entry?.display || entry?.text || entry?.label || '').trim();
        const postcode = String(entry?.postcode || entry?.postalCode || entry?.__postcode || '').trim();
        const name = String(entry?.name || '').trim();
        return {
            placeId,
            display,
            postcode: (/^\d{5}$/.test(postcode) ? postcode : ''),
            name,
            tsUsed: Number(entry?.tsUsed || entry?.ts || 0) || 0,
            tsSaved: Number(entry?.tsSaved || 0) || 0
        };
    }
    function __addrBookIsSaved(placeId) {
        const pid = String(placeId || '').trim();
        if (!pid) return false;
        try { return (__addrBookLoad().saved || []).some(x => String(x?.placeId || '') === pid); } catch (_) { return false; }
    }
    function __addrBookUpsertRecent(entry) {
        const e = __addrBookNormalize(entry);
        if (!e || !e.display) return;
        const book = __addrBookLoad();
        const now = Date.now();

        // Update saved record if present
        const sIdx = (book.saved || []).findIndex(x => String(x?.placeId || '') === e.placeId);
        if (sIdx >= 0) {
            const cur = __addrBookNormalize(book.saved[sIdx]) || {};
            book.saved[sIdx] = { ...cur, placeId: e.placeId, display: e.display || cur.display || '', postcode: e.postcode || cur.postcode || '', name: cur.name || '', tsSaved: Number(cur.tsSaved || now) || now, tsUsed: now };
        }

        book.recent = (book.recent || []).filter(x => String(x?.placeId || '') !== e.placeId);
        book.recent.unshift({ placeId: e.placeId, display: e.display, postcode: e.postcode, tsUsed: now });
        book.recent = (book.recent || []).slice(0, __ADDR_BOOK_MAX_RECENT);
        __addrBookSave(book);
    }
    function __addrBookUpsertSaved(entry, name) {
        const e = __addrBookNormalize(entry);
        const n = String(name || '').trim();
        if (!e || !e.display || !n) return;

        const book = __addrBookLoad();
        const now = Date.now();
        const idx = (book.saved || []).findIndex(x => String(x?.placeId || '') === e.placeId);
        if (idx >= 0) {
            const cur = __addrBookNormalize(book.saved[idx]) || {};
            book.saved[idx] = { ...cur, placeId: e.placeId, display: e.display || cur.display || '', postcode: e.postcode || cur.postcode || '', name: n, tsSaved: Number(cur.tsSaved || now) || now, tsUsed: now };
        } else {
            book.saved = Array.isArray(book.saved) ? book.saved : [];
            book.saved.unshift({ placeId: e.placeId, display: e.display, postcode: e.postcode, name: n, tsSaved: now, tsUsed: now });
        }
        book.saved = (book.saved || []).filter(x => x && x.placeId && x.display);
        book.saved.sort((a, b) => Number(b.tsSaved || 0) - Number(a.tsSaved || 0));
        book.saved = book.saved.slice(0, __ADDR_BOOK_MAX_SAVED);

        __addrBookSave(book);
        __addrBookUpsertRecent(e);
    }
    function __addrBookRemove(placeId) {
        const pid = String(placeId || '').trim();
        if (!pid) return;
        const book = __addrBookLoad();
        book.saved = (book.saved || []).filter(x => String(x?.placeId || '') !== pid);
        book.recent = (book.recent || []).filter(x => String(x?.placeId || '') !== pid);
        __addrBookSave(book);
    }
    function __addrBookSections() {
        const book = __addrBookLoad();
        const saved = (book.saved || []).map(__addrBookNormalize).filter(Boolean);
        const savedIds = new Set(saved.map(x => x.placeId));
        const recent = (book.recent || []).map(__addrBookNormalize).filter(x => x && !savedIds.has(x.placeId));
        saved.sort((a, b) => Number(b.tsSaved || 0) - Number(a.tsSaved || 0));
        recent.sort((a, b) => Number(b.tsUsed || 0) - Number(a.tsUsed || 0));
        return { saved, recent };
    }


    function __addrBookSavedName(placeId) {
        const pid = String(placeId || '').trim();
        if (!pid) return '';
        try {
            const { saved } = __addrBookSections();
            const hit = (saved || []).find(x => String(x?.placeId || '').trim() === pid);
            return String(hit?.name || '').trim();
        } catch (_) { return ''; }
    }
    function __addrKey(str) {
        return String(str || '')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .replace(/\u00A0/g, ' ')
            .replace(/[’']/g, "'")
            .trim();
    }


function __addrKeyLoose(str) {
  return __addrKey(str)
    .replace(/[.,;:()\[\]{}]/g, ' ')
    .replace(/[\/\\\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Best match from Address Book for pinning (exact or "similar" while typing)
function __addrBookBestMatch(typed) {
  const raw = String(typed || '').trim();
  if (!raw) return null;

  const t = __addrKeyLoose(__cleanAddress(raw));
  const tCompactLen = t.replace(/\s/g, '').length;
  if (!t) return null;

  const { saved, recent } = __addrBookSections();

  const scoreOf = (entry) => {
    const cand = __addrKeyLoose(__cleanAddress(String(entry?.display || '')));
    if (!cand) return 0;

    // Exact match
    if (cand === t) return 100;

    // only allow fuzzy when user typed enough (>= 3 non-space chars)
    if (tCompactLen < 3) return 0;

    // Strong prefix relationships feel the most natural while typing
    if (cand.startsWith(t)) return 92;
    if (t.startsWith(cand)) return 88;

    // Contains is still a good signal
    if (cand.includes(t)) return 82;

    // Token overlap (handles reorderings)
    const toks = t.split(' ').filter(Boolean).filter(x => x.length >= 2);
    if (!toks.length) return 0;

    let hit = 0;
    for (const tok of toks) {
      if (cand.includes(tok)) hit++;
    }
    const ratio = hit / toks.length;

    if (ratio >= 0.8) return 78;
    if (ratio >= 0.6) return 72;
    if (ratio >= 0.5 && toks.length >= 3) return 70;

    return 0;
  };

  let best = null;
  let bestScope = '';
  let bestScore = 0;

  const scan = (arr, scope) => {
    for (const e of (arr || [])) {
      const sc = scoreOf(e);
      if (sc > bestScore || (sc === bestScore && best && bestScope === 'recent' && scope === 'saved')) {
        best = e; bestScope = scope; bestScore = sc;
      }
    }
  };

  scan(saved, 'saved');
  scan(recent, 'recent');

  if (!best || !best?.placeId) return null;

  // Threshold: always allow exact; otherwise require decent score (prefix/contains)
  if (bestScore < 70 && bestScore !== 100) return null;

  return { entry: best, scope: bestScope, score: bestScore };
}

            // SVG icons (inline, scalable, theme-friendly)
            const __SVG_STAR_OUTLINE = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 17.27l-5.18 3.05 1.4-5.98L3 9.24l6.07-.52L12 3l2.93 5.72 6.07.52-5.22 5.1 1.4 5.98z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>`;
            const __SVG_STAR_FILLED  = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 17.27l-5.18 3.05 1.4-5.98L3 9.24l6.07-.52L12 3l2.93 5.72 6.07.52-5.22 5.1 1.4 5.98z" fill="currentColor"/></svg>`;
            const __SVG_TRASH = `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M4 6H20M16 6L15.7294 5.18807C15.4671 4.40125 15.3359 4.00784 15.0927 3.71698C14.8779 3.46013 14.6021 3.26132 14.2905 3.13878C13.9376 3 13.523 3 12.6936 3H11.3064C10.477 3 10.0624 3 9.70951 3.13878C9.39792 3.26132 9.12208 3.46013 8.90729 3.71698C8.66405 4.00784 8.53292 4.40125 8.27064 5.18807L8 6M18 6V16.2C18 17.8802 18 18.7202 17.673 19.362C17.3854 19.9265 16.9265 20.3854 16.362 20.673C15.7202 21 14.8802 21 13.2 21H10.8C9.11984 21 8.27976 21 7.63803 20.673C7.07354 20.3854 6.6146 19.9265 6.32698 19.362C6 18.7202 6 17.8802 6 16.2V6M14 10V17M10 10V17" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>`;
  
    function __ensureAddrNameModal() {
        if (window.__homeAddrNameModal) return window.__homeAddrNameModal;

        const overlay = document.createElement('div');
        overlay.className = 'addr-modal-overlay';
        overlay.innerHTML = `
          <div class="addr-modal" role="dialog" aria-modal="true" aria-labelledby="addrHomeModalTitle">
            <div class="addr-modal-head" id="addrHomeModalTitle">Salva indirizzo</div>
            <div class="addr-modal-body">
              <label class="addr-modal-label" for="addrHomeModalInput">Nome</label>
              <input id="addrHomeModalInput" class="addr-modal-input" type="text" inputmode="text" autocomplete="off" autocapitalize="words" />
            </div>
            <div class="addr-modal-foot">
              <button type="button" class="addr-modal-btn cancel">Annulla</button>
              <button type="button" class="addr-modal-btn confirm">Salva</button>
            </div>
          </div>
        `;
        document.body.appendChild(overlay);

        const titleEl = overlay.querySelector('#addrHomeModalTitle');
        const inputEl = overlay.querySelector('#addrHomeModalInput');
        const btnCancel = overlay.querySelector('.addr-modal-btn.cancel');
        const btnOk = overlay.querySelector('.addr-modal-btn.confirm');

        let __resolve = null;
        let __prevOverflow = '';
        let __scrollY = 0;
        let __prevBody = null;
        let __vvCleanup = null;

        const __lockScroll = () => {
          try {
            __prevOverflow = document.documentElement.style.overflow || '';
            document.documentElement.style.overflow = 'hidden';
          } catch (_) { }
          // iOS-friendly: lock body scroll without layout jump
          try {
            __scrollY = window.scrollY || window.pageYOffset || 0;
            __prevBody = {
                position: document.body.style.position,
                top: document.body.style.top,
                left: document.body.style.left,
                right: document.body.style.right,
                width: document.body.style.width
            };
            document.body.style.position = 'fixed';
            document.body.style.top = (-__scrollY) + 'px';
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
          } catch (_) { }
        };

        const __unlockScroll = () => {
          try { document.documentElement.style.overflow = __prevOverflow || ''; } catch (_) { }
          try {
            if (__prevBody) {
                document.body.style.position = __prevBody.position || '';
                document.body.style.top = __prevBody.top || '';
                document.body.style.left = __prevBody.left || '';
                document.body.style.right = __prevBody.right || '';
                document.body.style.width = __prevBody.width || '';
            }
            window.scrollTo(0, __scrollY || 0);
          } catch (_) { }
          __prevBody = null;
        };

        const __syncVV = () => {
          try {
            const vv = window.visualViewport;
            const vvH = (vv && vv.height) ? vv.height : window.innerHeight;
            overlay.style.setProperty('--addrVVH', vvH + 'px');

            // Move modal up when the virtual keyboard reduces the visual viewport
            const layoutH = window.innerHeight || vvH;
            const centerVV = (vv && typeof vv.offsetTop === 'number') ? (vv.offsetTop + vvH / 2) : (vvH / 2);
            const centerLayout = layoutH / 2;
            let shift = centerVV - centerLayout; // negative => move up
            // Only allow upward shift, cap to avoid going off-screen
            if (shift > 0) shift = 0;
            if (shift < -260) shift = -260;
            overlay.style.setProperty('--addrModalShift', shift + 'px');
          } catch (_) { }
        };

        const __attachVV = () => {
          if (__vvCleanup) return;
          let raf = 0;
          const onVV = () => {
            try { if (raf) cancelAnimationFrame(raf); } catch (_) { }
            raf = requestAnimationFrame(() => { __syncVV(); });
          };

          const vv = window.visualViewport;
          try { window.addEventListener('resize', onVV, { passive: true }); } catch (_) { }
          try { window.addEventListener('orientationchange', onVV, { passive: true }); } catch (_) { }

          if (vv && vv.addEventListener) {
            try { vv.addEventListener('resize', onVV, { passive: true }); } catch (_) { }
            try { vv.addEventListener('scroll', onVV, { passive: true }); } catch (_) { }
          }

          __vvCleanup = () => {
            try { window.removeEventListener('resize', onVV); } catch (_) { }
            try { window.removeEventListener('orientationchange', onVV); } catch (_) { }
            if (vv && vv.removeEventListener) {
                try { vv.removeEventListener('resize', onVV); } catch (_) { }
                try { vv.removeEventListener('scroll', onVV); } catch (_) { }
            }
            try { if (raf) cancelAnimationFrame(raf); } catch (_) { }
            raf = 0;
            __vvCleanup = null;
          };

          // initial
          setTimeout(__syncVV, 0);
          setTimeout(__syncVV, 80);
        };

        const __detachVV = () => {
          try { if (__vvCleanup) __vvCleanup(); } catch (_) { }
          __vvCleanup = null;
          try { overlay.style.removeProperty('--addrModalShift'); } catch (_) { }
          try { overlay.style.removeProperty('--addrVVH'); } catch (_) { }
        };

        
        const close = (val) => {
            try { overlay.style.display = 'none'; } catch (_) { }
            try { __detachVV(); } catch (_) { }
            try { __unlockScroll(); } catch (_) { }
            const r = __resolve;
            __resolve = null;
            if (typeof r === 'function') r(val);
        };

        const syncBtn = () => { btnOk.disabled = !String(inputEl.value || '').trim(); };

        overlay.addEventListener('click', (ev) => { if (ev.target === overlay) close(null); });
        btnCancel.addEventListener('click', () => close(null));
        btnOk.addEventListener('click', () => { const v = String(inputEl.value || '').trim(); if (!v) return; close(v); });
        inputEl.addEventListener('input', syncBtn);
        inputEl.addEventListener('keydown', (ev) => {
            if (ev.key === 'Escape') { ev.preventDefault(); close(null); return; }
            if (ev.key === 'Enter') { ev.preventDefault(); const v = String(inputEl.value || '').trim(); if (!v) return; close(v); }
        });

        window.__homeAddrNameModal = {
            open: ({ title, initialValue, confirmText } = {}) => new Promise((resolve) => {
                __resolve = resolve;
                try { __lockScroll(); } catch (_) { }
                try { titleEl.textContent = String(title || 'Salva indirizzo'); } catch (_) { }
                try { btnOk.textContent = String(confirmText || 'Salva'); } catch (_) { }
                try { inputEl.value = String(initialValue || ''); } catch (_) { }
                syncBtn();
                try { overlay.style.display = 'flex'; } catch (_) { }
                try { __attachVV(); } catch (_) { }
                setTimeout(() => { try { __syncVV(); } catch (_) { } }, 0);
                setTimeout(() => { try { inputEl.focus(); inputEl.select(); } catch (_) { } }, 60);
            })
        };
        return window.__homeAddrNameModal;
    }

    function __ensureAddrQuickStyles() {
        if (document.getElementById('addr-quick-style')) return;
        const style = document.createElement('style');
        style.id = 'addr-quick-style';
        style.textContent = `
          .addr-quick{padding:10px 10px}
          .addr-quick-section{margin-top:6px}
          .addr-quick-head{display:flex;align-items:center;justify-content:space-between;font-size:11px;letter-spacing:.08em;text-transform:uppercase;opacity:.7;padding:6px 4px}
          .addr-quick-empty{padding:12px 14px;font-size:13px;opacity:.7}
          .addr-quick-item{display:flex;align-items:center;gap:10px}
          .addr-actions{margin-left:auto;display:flex;gap:8px;flex:0 0 auto}
          .addr-act{width:32px;height:32px;border-radius:999px;border:0;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;background:rgba(17,24,39,0.06);color:rgba(17,24,39,0.85)}
          .addr-act svg{width:18px;height:18px;display:block}
          .addr-act:hover{background:rgba(17,24,39,0.10)}
          .addr-act:active{transform:scale(0.96)}
          .addr-act.addr-del{background:rgba(255,77,117,0.12);color:#ff4d75}
        

          .addr-saved-chip{display:inline-flex;align-items:center;justify-content:center;max-width:140px;overflow:hidden;text-overflow:ellipsis;padding:6px 10px;border-radius:999px;background:rgba(255,77,117,0.14);color:#ff4d75;font-weight:900;font-size:12px;line-height:1;white-space:nowrap}
          .addr-item.is-pinned{outline:2px solid rgba(255,77,117,0.22);outline-offset:2px}

          .addr-modal-overlay{position:fixed;inset:0;z-index:2147483000;display:none;align-items:stretch;justify-content:stretch;padding:calc(16px + env(safe-area-inset-top)) 16px calc(16px + env(safe-area-inset-bottom));background:rgba(17,24,39,0.52);backdrop-filter:saturate(120%) blur(6px);overflow:auto;-webkit-overflow-scrolling:touch}
          .addr-modal{width:min(440px,100%);border-radius:18px;background:#fff;box-shadow:0 28px 80px rgba(0,0,0,0.30);overflow:hidden;display:flex;flex-direction:column;margin:auto;max-height:calc(var(--addrVVH, 100vh) - 32px);transform:translateY(var(--addrModalShift, 0px));transition:transform .18s ease, box-shadow .18s ease}
          [data-theme="dark"] .addr-modal{background:rgba(17,24,39,0.96);box-shadow:0 28px 90px rgba(0,0,0,0.55)}
          .addr-modal-head{padding:14px 16px 10px;font-weight:900;font-size:16px;line-height:1.2;color:#111827}
          [data-theme="dark"] .addr-modal-head{color:#fff}
          .addr-modal-body{padding:0 16px 14px;overflow:auto}
          .addr-modal-label{display:block;font-size:12px;opacity:.75;margin:0 0 8px}
          .addr-modal-input{width:100%;border-radius:14px;border:1px solid rgba(17,24,39,0.14);padding:12px 14px;font-size:16px;line-height:1.2;outline:none}
          .addr-modal-input:focus{border-color:rgba(255,77,117,0.65);box-shadow:0 0 0 4px rgba(255,77,117,0.18)}
          [data-theme="dark"] .addr-modal-input{background:rgba(255,255,255,0.06);border-color:rgba(255,255,255,0.16);color:#fff}
          [data-theme="dark"] .addr-modal-input:focus{border-color:rgba(255,77,117,0.75);box-shadow:0 0 0 4px rgba(255,77,117,0.22)}
          .addr-modal-foot{display:flex;gap:10px;justify-content:flex-end;padding:0 16px 16px}
          .addr-modal-btn{border-radius:999px;border:0;cursor:pointer;font-weight:900;font-size:14px;line-height:1;padding:12px 16px}
          .addr-modal-btn:active{transform:translateY(1px)}
          .addr-modal-btn.cancel{background:rgba(17,24,39,0.08);color:rgba(17,24,39,0.86)}
          [data-theme="dark"] .addr-modal-btn.cancel{background:rgba(255,255,255,0.10);color:rgba(255,255,255,0.92)}
          .addr-modal-btn.confirm{background:var(--ds-gradient);color:#fff;box-shadow:0 14px 34px rgba(255,77,117,0.22)}
          .addr-modal-btn.confirm[disabled]{opacity:.55;cursor:not-allowed;box-shadow:none}
          @media (max-width:520px){.addr-modal-overlay{padding:calc(14px + env(safe-area-inset-top)) 14px calc(14px + env(safe-area-inset-bottom))}.addr-modal{width:100%;max-width:420px;border-radius:18px}}
          .addr-cap-chip{display:none !important}
`;
        document.head.appendChild(style);
    }

    function __renderAddressQuickList(container, input, { onPick } = {}) {
        if (!container) return false;
        __ensureAddrQuickStyles();

        let { saved, recent } = __addrBookSections();

        // Pin best match (exact or "similar" while typing) at top until it changes
const m = __addrBookBestMatch(String(input && input.value || ''));
let pinned = null;
let pinnedScope = '';
if (m && m.entry) {
    pinned = m.entry;
    pinnedScope = m.scope || '';
    if (pinnedScope === 'saved') {
        saved = (saved || []).filter(x => String(x && x.placeId || '') !== String(pinned.placeId || ''));
    } else if (pinnedScope === 'recent') {
        recent = (recent || []).filter(x => String(x && x.placeId || '') !== String(pinned.placeId || ''));
    }
}
if ((saved.length + recent.length) === 0) {
            container.style.display = 'none';
            container.innerHTML = '';
            return false;
        }

        const itemHtml = (e, scope) => {
            const s = __formatAddrSuggestion(e || {});
            const placeId = String(e.placeId || '').trim();
            const display = String(e.display || s.display || '').trim();
            const main = String(s.__mainShort || display.split(',')[0] || display).trim();
            const sub = String(s.__subNoCap || '').trim();
            const cap = String(e.postcode || s.__postcode || '').trim();
            const name = String(e.name || '').trim();
            const hasCap = /^\d{5}$/.test(cap);

            const mainLine = escapeHtml(main);
            const subText = escapeHtml(sub || display);
            const nameChip = (scope === 'saved' && name) ? `<span class="addr-saved-chip" title="${escapeHtml(name)}">${escapeHtml(name)}</span>` : ``;
            const fullSpan = display ? `<span class="addr-subtext">${escapeHtml(display)}</span>` : (subText ? `<span class="addr-subtext">${subText}</span>` : ``);

            return `
              <div class="addr-item addr-quick-item" data-placeid="${escapeHtml(placeId)}" data-scope="${escapeHtml(scope)}">
                <div class="addr-lines">
                  <div class="addr-sub">${fullSpan}</div>
                </div>
                <div class="addr-actions">
                  ${nameChip}
                  <button type="button" class="addr-act addr-save" data-act="save" title="${__addrBookIsSaved(placeId) ? 'Rinomina' : 'Salva'}" aria-label="${__addrBookIsSaved(placeId) ? 'Rinomina' : 'Salva'}">${__addrBookIsSaved(placeId) ? __SVG_STAR_FILLED : __SVG_STAR_OUTLINE}</button>
                  <button type="button" class="addr-act addr-del" data-act="del" title="Elimina" aria-label="Elimina">${__SVG_TRASH}</button>
                </div>
              </div>
            `;
        };

        const htmlSaved = saved.length ? saved.map(e => itemHtml(e, 'saved')).join('') : `<div class="addr-quick-empty">Nessun indirizzo salvato.</div>`;
        const htmlRecent = recent.length ? recent.map(e => itemHtml(e, 'recent')).join('') : `<div class="addr-quick-empty">Nessun indirizzo recente.</div>`;

        const htmlPinned = pinned
            ? `<div class="addr-quick-section">
                 <div class="addr-quick-head">Già presente</div>
                 ${itemHtml(pinned, pinnedScope).replace('class="addr-item', 'class="addr-item is-pinned')}
               </div>`
            : ``;

        container.style.display = 'block';
        container.innerHTML = `
          <div class="addr-quick">
            ${htmlPinned}
            <div class="addr-quick-section">
              <div class="addr-quick-head">Salvati</div>
              ${htmlSaved}
            </div>
            <div class="addr-quick-section">
              <div class="addr-quick-head">Recenti</div>
              ${htmlRecent}
            </div>
          </div>
        `;

        container.querySelectorAll('.addr-quick-item').forEach((row) => {
            const placeId = String(row.getAttribute('data-placeid') || '').trim();
            const scope = String(row.getAttribute('data-scope') || '').trim();
            const entry = (() => {
          const { saved, recent } = __addrBookSections();
          const all = (saved || []).concat(recent || []);
          return all.find(x => String(x?.placeId || '') === placeId) || null;
        })();

            row.addEventListener('click', (ev) => {
                const t = ev && ev.target;
                if (t && t.closest && t.closest('button.addr-act')) return;
                if (!entry) return;

                const picked = { placeId: entry.placeId, display: entry.display, __postcode: entry.postcode || '' };
                try { input.dataset.selected = JSON.stringify(picked); } catch (_) {}
                try { input.dataset.cap = String(entry.postcode || ''); } catch (_) {}
                input.value = String(entry.display || '');

                try { __addrBookUpsertRecent(entry); } catch (_) {}
                try { onPick && onPick(picked); } catch (_) {}
            }, { passive: false });

            const saveBtn = row.querySelector('button.addr-save');
            if (saveBtn) saveBtn.addEventListener('click', async (ev) => {
                ev.preventDefault(); ev.stopPropagation();
                if (!entry) return;
                const isSaved = __addrBookIsSaved(entry.placeId);
                const cur = (__addrBookSections().saved || []).find(x => x.placeId === entry.placeId) || entry;
                const modal = __ensureAddrNameModal();
                const name = await modal.open({
                    title: (isSaved ? 'Rinomina indirizzo' : 'Salva indirizzo'),
                    initialValue: String(cur.name || ''),
                    confirmText: (isSaved ? 'Rinomina' : 'Salva')
                });
                if (name == null) return;
                const n = String(name || '').trim();
                if (!n) return;
                __addrBookUpsertSaved(entry, n);
                __renderAddressQuickList(container, input, { onPick });
            }, { passive: false });

            const delBtn = row.querySelector('button.addr-del');
            if (delBtn) delBtn.addEventListener('click', (ev) => {
                ev.preventDefault(); ev.stopPropagation();
                __addrBookRemove(placeId);
                __renderAddressQuickList(container, input, { onPick });
            }, { passive: false });
        });

        return true;
    }

// ───────────────────────────────────────────────────────────────
// Address suggestions: preserve Google's relevance order. Civic validation is
// performed only after selection by addressResolve, not by UI heuristics.
// ───────────────────────────────────────────────────────────────
function __romePriorityText(txt) {
    const s = String(txt || '').toLowerCase();
    if (/\broma\b/.test(s) || /\brm\b/.test(s)) return 2;
    if (s.includes('lazio')) return 1;
    return 0;
}
function __isStreetLikeText(txt) {
    const t = String(txt || '').trim();
    if (!t) return false;
    if (/\d/.test(t)) return true;
    return /\b(via|viale|piazza|p\.za|p\.le|piazzale|largo|corso|vicolo|strada|borgo|lungotevere|lungomare|galleria|circonvallazione|salita|discesa|rampa|passaggio|rotatoria|contrada)\b/i.test(t);
}
function __filterAndSortSuggestions(list) {
    const arr = Array.isArray(list) ? list : [];
    return arr.filter((item, index) => {
        const placeId = String(item?.placeId || item?.place_id || '').trim();
        return !!placeId && arr.findIndex((candidate) => String(candidate?.placeId || candidate?.place_id || '').trim() === placeId) === index;
    });
}

    function getCapState() {
        try {
            if (window.FlorameHomeAddress && typeof window.FlorameHomeAddress.readState === 'function') {
                return window.FlorameHomeAddress.readState();
            }
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
            if (!saved || !saved.cap) return { verified: false };
            const isFresh = (Date.now() - (saved.ts || 0)) < CONFIG.capValidityMs;
            return { ...saved, fresh: isFresh };
        } catch { return { verified: false }; }
    }

    // --- MAIN INITIALIZATION ---
    function initInlineModule() {
        const input = $('capAddressInput');
        if (!input) return;

        if($('capUseLocationBtn')) $('capUseLocationBtn').onclick = useCurrentLocation;
        
        setupAutocomplete('capAddressInput', 'capAddrSuggest');
        syncUI();
                // ✅ HOME: mostra subito la quicklist (Salvati/Recenti) senza aspettare il focus
        try {
          const st = getCapState();
          if (!st || !st.verified || !st.fresh) {
            if (typeof input.__addrShowQuick === 'function') input.__addrShowQuick({ force: true });
          }
        } catch (_) {}
// ✅ Auto-refresh: se l'indirizzo è stato ripristinato, rilancia la verifica stock
        setTimeout(autoReverifyAfterRestore, 0);
    }

    async function useCurrentLocation() {
        const button = $('capUseLocationBtn');
        const input = $('capAddressInput');
        const label = button?.querySelector('span');
        const idleLabel = label?.textContent || 'Usa la mia posizione';
        if (!navigator.geolocation) {
            showError('La posizione non è disponibile su questo dispositivo. Inserisci l’indirizzo.');
            return;
        }
        if (button) {
            button.disabled = true;
            button.setAttribute('aria-busy', 'true');
        }
        if (label) label.textContent = 'Trovo l’indirizzo…';
        showError(null);
        try {
            const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000
                });
            });
            const lat = Number(position.coords.latitude);
            const lng = Number(position.coords.longitude);
            const url = new URL(CONFIG.addressResolve, window.location.origin);
            url.searchParams.set('lat', String(lat));
            url.searchParams.set('lng', String(lng));
            const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
            const data = await response.json().catch(() => ({}));
            const location = data?.place || {};
            const cap = String(location.postcode || location.postalCode || '').trim();
            const display = __canonicalItalianAddress(location.display || location.formattedAddress || '', location.addressComponents || []);
            if (!response.ok || !/^\d{5}$/.test(cap) || !display) throw new Error('gps_address_unresolved');
            const hasCivic = location.isAddress === true && Boolean(String(location.houseNumber || __addressComponentValue(location.addressComponents || [], 'street_number')).trim());
            if (!hasCivic) {
                if (input) input.value = display;
                showError('Ho trovato la via, ma non il civico. Aggiungilo e scegli l’indirizzo completo.');
                input?.focus();
                return;
            }
            if (input) {
                input.value = display;
                input.dataset.cap = cap;
            }
            try {
                localStorage.setItem(ADDR_CACHE_KEY, JSON.stringify({ display, postcode: cap, lat, lng, source: 'gps', ts: Date.now() }));
            } catch (_) {}
            await verifyStock(cap, display, {
                userInitiated: true,
                context: 'home_florame',
                source: 'precise_location',
                query: 'gps',
                display,
                resolvedDisplay: display,
                 formattedAddress: display,
                 addressComponents: location.addressComponents || [],
                 placeId: location.placeId || '',
                 street: location.street || '',
                 houseNumber: location.houseNumber || '',
                 city: location.city || '',
                 province: location.province || '',
                lat,
                lng,
                isAddress: true
            });
        } catch (error) {
            const denied = Number(error?.code || 0) === 1;
            showError(denied
                ? 'Permesso posizione negato. Puoi inserire l’indirizzo manualmente.'
                : 'Non riesco a ricavare l’indirizzo esatto. Inseriscilo manualmente.');
        } finally {
            if (button) {
                button.disabled = false;
                button.removeAttribute('aria-busy');
            }
            if (label) label.textContent = idleLabel;
        }
    }

    // --- AUTOCOMPLETE LOGIC (Unified Style) ---
    function setupAutocomplete(inputId, suggestId) {
        const input = $(inputId);
        const suggest = $(suggestId);
        if (!input || !suggest) return;
        if (input.__addrWired) return;
        input.__addrWired = true;

        let debounceTimer;
        let autocompleteController = null;
        let autocompleteSequence = 0;
        const autocompleteCache = new Map();
        let __suppressQuickReopenUntil = 0;

        const suppressQuickReopenOnce = () => {
            __suppressQuickReopenUntil = Date.now() + 400;
        };

        try { suggest.addEventListener('pointerdown', suppressQuickReopenOnce, { passive: true, capture: true }); } catch (_) {}
        try { suggest.addEventListener('touchstart', suppressQuickReopenOnce, { passive: true, capture: true }); } catch (_) {}
        try { suggest.addEventListener('mousedown', suppressQuickReopenOnce, { passive: true, capture: true }); } catch (_) {}

        // 1. Clear Button Logic (X)
        const wrapper = input.closest('.cap-input-row') || input.parentElement;
        let clearBtn = wrapper.querySelector('.cap-clear-btn');
        if (!clearBtn) {
            clearBtn = document.createElement('button');
            clearBtn.className = 'cap-clear-btn';
            clearBtn.innerHTML = '&times;';
            wrapper.appendChild(clearBtn);
            
            const toggleClear = () => { clearBtn.style.display = input.value ? 'flex' : 'none'; };
            input.addEventListener('input', toggleClear);
            clearBtn.addEventListener('click', () => {
                autocompleteSequence += 1;
                try { autocompleteController?.abort?.(); } catch (_) {}
                input.value = '';
                try { delete input.dataset.selected; } catch(e) {}
                try { delete input.dataset.cap; } catch(e) {}
                clearBtn.style.display = 'none';
                suggest.style.display = 'none';
                showError(null);
                input.focus();
                resetSessionToken(input);
                try {
                    if (typeof input.__addrShowQuick === 'function') {
                        input.__addrShowQuick({ force: true });
                    }
                } catch (_) {}
            });
        }

        
        // Tap-to-see: mostra lista rapida Recenti + Salvati solo a campo vuoto.
        // Con { force:true } la apre anche se il campo ha già testo (utile all'avvio, prima del focus).
        let __quickOpenGuardTs = 0;
        const showQuickIfNeeded = (opts = {}) => {
            try {
                const skipGuard = !!(opts && opts.skipGuard);
                const now = Date.now();
                if (!skipGuard) {
                    if (now - __quickOpenGuardTs < 160) return false;
                    __quickOpenGuardTs = now;
                }

                const force = !!(opts && opts.force);
                const qt = String(input.value || '').trim();

                if (!force && qt) return false;

                const rendered = __renderAddressQuickList(suggest, input, {
                    onPick: (picked) => {
                        suggest.style.display = 'none';
                        suggest.innerHTML = '';
                        try { resolveAddress(String(picked?.placeId || ''), input, picked || {}); } catch (_) {}
                    }
                });
                return !!rendered;
            } catch (_) {}
            return false;
        };

        const shouldKeepQuickListVisible = () => {
            try {
                if (!input || !suggest || input.disabled) return false;
                if (!input.isConnected || !suggest.isConnected) return false;
                if (String(input.value || '').trim()) return false;
                if (typeof input.getClientRects === 'function' && input.getClientRects().length === 0) return false;
                return true;
            } catch (_) {}
            return false;
        };

        const syncQuickListWhenEmpty = () => {
            try {
                if (Date.now() < __suppressQuickReopenUntil) return false;
                if (!shouldKeepQuickListVisible()) return false;
                return !!showQuickIfNeeded({ force: true, skipGuard: true });
            } catch (_) {}
            return false;
        };

        const scheduleQuickListSync = () => {
            setTimeout(() => {
                try { syncQuickListWhenEmpty(); } catch (_) {}
            }, 0);
        };

        try { input.__addrShowQuick = showQuickIfNeeded; } catch (_) {}

        const openQuickListAfterNativeFocus = () => {
            requestAnimationFrame(() => {
                try {
                    if (document.activeElement === input) showQuickIfNeeded();
                } catch (_) {}
            });
        };

        input.addEventListener('focus', openQuickListAfterNativeFocus, { passive: true });
        input.addEventListener('blur', () => {
            const query = String(input.value || '').trim();
            if (query) return;

            scheduleQuickListSync();
        }, { passive: true });

        document.addEventListener('click', scheduleQuickListSync, true);

// 2. Input Event
        input.addEventListener('input', () => {
            clearTimeout(debounceTimer);
            autocompleteSequence += 1;
            const sequence = autocompleteSequence;
            try { autocompleteController?.abort?.(); } catch (_) {}
            showError(null);
            const query = input.value.trim();
            
            if (!query.length) {
                // Campo vuoto: ripristina Recenti + Salvati, se esistono.
                try {
                    __renderAddressQuickList(suggest, input, {
                        onPick: (picked) => {
                            suggest.style.display = 'none';
                            suggest.innerHTML = '';
                            try { resolveAddress(String(picked?.placeId || ''), input, picked || {}); } catch (_) {}
                        }
                    });
                } catch (_) { suggest.style.display = 'none'; }
                return;
            }

            if (query.length < 3) {
                try { suggest.style.display = 'none'; suggest.innerHTML = ''; } catch (_) {}
                return;
            }

            const cacheKey = query.toLocaleLowerCase('it-IT').replace(/\s+/g, ' ');
            const cached = autocompleteCache.get(cacheKey);
            if (cached && Date.now() - cached.savedAt < 90 * 1000) {
                renderSuggestions(cached.list, suggest, input);
                clearBtn.style.display = 'flex';
                return;
            }

            // UI: Show loading inside suggestion box
            __renderAddressPinnedLoading(suggest, input);

            debounceTimer = setTimeout(async () => {
                const token = ensureSessionToken(input);
                autocompleteController = typeof AbortController === 'function' ? new AbortController() : null;
                try {
                    // Unified Endpoint URL format
                    const url = `${CONFIG.addressAutocomplete}?q=${encodeURIComponent(query)}&limit=5&country=IT&sessionToken=${encodeURIComponent(token)}`;
                    const res = await fetch(url, {
                        signal: autocompleteController?.signal,
                        headers: { Accept: 'application/json' }
                    });
                    if (!res.ok) throw new Error(`Autocomplete HTTP ${res.status}`);
                    const data = await res.json();
                    if (sequence !== autocompleteSequence || String(input.value || '').trim() !== query) return;
                    const list = Array.isArray(data?.suggestions) ? data.suggestions : (Array.isArray(data?.predictions) ? data.predictions : []);
                    autocompleteCache.set(cacheKey, { list, savedAt: Date.now() });
                    if (autocompleteCache.size > 24) autocompleteCache.delete(autocompleteCache.keys().next().value);
                    renderSuggestions(list, suggest, input);
                } catch (e) { 
                    if (e?.name !== 'AbortError') {
                        console.error("Autocomplete error", e);
                        if (sequence === autocompleteSequence) suggest.style.display = 'none';
                    }
                } finally {
                    if (sequence === autocompleteSequence) clearBtn.style.display = 'flex';
                }
            }, 90);
        });
    }


    function __syncAddrSuggestMetrics(suggestEl) {
      if (!suggestEl) return;
      try {
        const probe = document.createElement('div');
        probe.className = 'addr-item';
        probe.style.position = 'fixed';
        probe.style.left = '-9999px';
        probe.style.top = '0';
        probe.style.visibility = 'hidden';
        probe.style.pointerEvents = 'none';
        probe.innerHTML = `
          <div class="addr-lines">
            <div class="addr-sub"><span class="addr-subtext">Via Alessandro Manzoni 123, 00100 Roma RM, Italia</span></div>
          </div>
          <div class="addr-right"><span class="addr-saved-chip">Casa</span></div>
        `;
        document.body.appendChild(probe);
        const h = Math.round((probe.getBoundingClientRect && probe.getBoundingClientRect().height) ? probe.getBoundingClientRect().height : (probe.offsetHeight || 0));
        probe.remove();
        if (h && h > 0) suggestEl.style.setProperty('--addrItemH', h + 'px');
      } catch(_) {}
    }

    function __renderAddressSuggestionsLoading(suggestEl) {
      if (!suggestEl) return;
      suggestEl.style.display = '';
      suggestEl.setAttribute('role', 'listbox');
      try { __syncAddrSuggestMetrics(suggestEl); } catch(_) {}
      try { suggestEl.setAttribute('data-skel','1'); } catch(_) {}
      // Skeleton (4 rows) to keep dropdown height stable inside modals/cards
      suggestEl.innerHTML = `
        <div class="addr-skel" role="option" aria-disabled="true">
          ${Array.from({ length: 4 }).map(() => `
            <div class="addr-skel-item">
              <div class="addr-skel-lines">
                <div class="addr-skel-line"></div>
                <div class="addr-skel-line small"></div>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }


    function __renderAddressPinnedLoading(suggestEl, input) {
      if (!suggestEl) return;
      const q = String(input && input.value || '').trim();
      let m = null;
      try { m = __addrBookBestMatch(q); } catch(_) {}
      if (!m || !m.entry || !m.entry.placeId) { __renderAddressSuggestionsLoading(suggestEl); return; }

      // keep dropdown stable: 1 pinned row + 3 skeleton rows = 4 rows total
      suggestEl.style.display = '';
      suggestEl.setAttribute('role', 'listbox');
      try { __syncAddrSuggestMetrics(suggestEl); } catch(_) {}
      try { suggestEl.setAttribute('data-skel','1'); } catch(_) {}

      const entry = m.entry;
      const pid = String(entry.placeId || '').trim();
      const full = String(entry.display || '').trim();
      const name = (m.scope === 'saved') ? String(entry.name || '').trim() : '';
      const chip = (m.scope === 'saved' && name) ? `<div class="addr-right"><span class="addr-saved-chip" title="${escapeHtml(name)}">${escapeHtml(name)}</span></div>` : '';
      const fullSpan = full ? `<span class="addr-subtext">${escapeHtml(full)}</span>` : '';

      suggestEl.innerHTML = `
        <div class="addr-item is-pinned" data-placeid="${escapeHtml(pid)}">
          <div class="addr-lines">
            <div class="addr-sub">${fullSpan}</div>
          </div>
          ${chip}
        </div>
        <div class="addr-skel" aria-hidden="true">
          ${Array.from({ length: 3 }).map(() => `
            <div class="addr-skel-item">
              <div class="addr-skel-lines">
                <div class="addr-skel-line"></div>
                <div class="addr-skel-line small"></div>
              </div>
            </div>
          `).join('')}
        </div>
      `;

      const row = suggestEl.querySelector('.addr-item.is-pinned');
      if (row) row.addEventListener('click', async () => {
        try { input.value = full; } catch(_) {}
        try { suggestEl.style.display = 'none'; } catch(_) {}
        try { await resolveAddress(pid, input, { placeId: pid, display: full }); } catch(_) {}
      });
    }


function renderSuggestions(list, container, input) {
    // 1. Filtra risultati API (Google)
    let filtered = __filterAndSortSuggestions(list);
    const q = String(input && input.value || '').trim();

    // 2. RECUPERA MATCH LOCALE (Salvati/Recenti)
    // Usiamo la funzione fixata sopra
    try {
        const m = __addrBookBestMatch(q);
        
        if (m && m.entry && m.entry.placeId) {
            const match = m.entry;
            const scope = m.scope || '';
            const pid = String(match.placeId || '').trim();
            const savedName = match.name || __addrBookSavedName(pid);

            if (pid) {
                // Rimuovi dai risultati API se è già presente (evita duplicati)
                filtered = filtered.filter(x => String(x.placeId || x.place_id || '').trim() !== pid);
                
                // Crea l'oggetto "Pinned"
                const pinned = {
                    placeId: pid,
                    display: match.display,
                    text: match.display, // Fallback per formattazione
                    // Campi speciali per il render
                    __postcode: match.postcode || '',
                    __fromBook: true,
                    __bookScope: scope,
                    __bookName: savedName
                };

                // METTI IN CIMA ALLA LISTA
                filtered.unshift(pinned);
            }
        }
    } catch (e) { console.warn("Local match error", e); }

    // 3. Se la lista combinata è vuota, mostra messaggio
    if (!filtered || filtered.length === 0) {
        try { container.removeAttribute('data-skel'); } catch(_) {}
        container.style.display = 'block';
        container.innerHTML = `<div class="addr-loading" style="padding:12px; font-size:13px; color:#999;">Nessun risultato. Prova a scrivere <b>via</b> + <b>numero civico</b>.</div>`;
        return;
    }
    
    // 4. Render HTML
    container.innerHTML = filtered.map((item, idx) => {
        const s = __formatAddrSuggestion(item);
        
        // Logica Chips (CAP o Civico)
        const hasCap = !!(s.__postcode && /^\d{5}$/.test(String(s.__postcode)));
        const hasNumber = (item.hasNumber === true || item.hasNumber === false) 
            ? item.hasNumber 
            : /\d/.test(s.__mainShort || s.display || '');

        // Chip destro (Nome salvato es. "Casa" o Taglio Preferiti)
        const isPinned = !!item.__fromBook;
        const bookName = (item.__bookName) ? String(item.__bookName) : '';
        
        let rightChip = '';
        if (bookName) {
            rightChip = `<div class="addr-right"><span class="addr-saved-chip" title="${escapeHtml(bookName)}">${escapeHtml(bookName)}</span></div>`;
        } else if (isPinned && item.__bookScope === 'recent') {
             // Opzionale: icona orologio per recenti
             // rightChip = `<div class="addr-right" style="opacity:0.5">↺</div>`;
        }

        const extraClass = isPinned ? ' is-pinned' : '';
        const secondary = Array.from(new Set(String(s.__subNoCap || '')
            .split(',')
            .map(part => part.trim().replace(/^Città metropolitana di Roma Capitale$/i, 'Roma'))
            .filter(part => part && !/^Italia$/i.test(part))))
            .slice(0, 3)
            .join(', ');

        return `
          <div class="addr-item${extraClass}" data-idx="${idx}">
            <div class="addr-lines">
              <div class="addr-main">${escapeHtml(String(s.__mainShort || s.display || '').trim())}</div>
              ${secondary ? `<div class="addr-sub"><span class="addr-subtext">${escapeHtml(secondary)}</span></div>` : ''}
            </div>
            ${rightChip}
          </div>`;
    }).join('');

    try { container.removeAttribute('data-skel'); } catch(_) {}
    container.style.display = 'block';

    // 5. Gestione Click
    container.querySelectorAll('.addr-item').forEach(el => {
        el.addEventListener('click', async () => {
            const item = filtered[el.dataset.idx];
            // Se è pinned, usiamo i suoi dati puliti
            const s = __formatAddrSuggestion(item);
            
            input.value = s.display;
            container.style.display = 'none';
            
            await resolveAddress(item.placeId, input, s);
        });
    });
}



    // --- RESOLVE LOGIC (The Fix) ---
    async function resolveAddress(placeId, input, suggestionObj) {
        setLoading(true);

        const queryNow = String((input && input.value) || (suggestionObj && suggestionObj.display) || '').trim();
        const pid = String(placeId || '').trim();
        const attemptId = __newAddressAttemptId('home_florame');

        try {
            const token = ensureSessionToken(input);
            const url = `${CONFIG.addressResolve}?placeId=${encodeURIComponent(pid)}&sessionToken=${encodeURIComponent(token)}`;
            
            const res = await fetch(url);
            const data = await res.json();
            const place = data.place || data || {};

            // 1. Extract CAP (robusto: backend + parsing testo)
            const resolvedCapRaw = String(place.postalCode || place.postcode || '').trim();
            const isAddress = !!place.isAddress; // Backend logic

            const addressComponents = Array.isArray(place.addressComponents) ? place.addressComponents : [];
            let resolvedDisplay = __canonicalItalianAddress(place.display || place.formattedAddress || suggestionObj.display || input.value, addressComponents);

            const guessedCap =
                (resolvedDisplay.match(/\b(\d{5})\b/) || [])[1] ||
                (String(suggestionObj && suggestionObj.display || '').match(/\b(\d{5})\b/) || [])[1] ||
                (String(input && input.value || '').match(/\b(\d{5})\b/) || [])[1] ||
                '';

            const resolvedCap = (/^\d{5}$/.test(resolvedCapRaw) ? resolvedCapRaw : (guessedCap || ''));
            const formattedAddress = String(place.formattedAddress || place.display || resolvedDisplay || '').trim();

            // Assicura che il CAP sia presente nel testo mostrato
            if (resolvedCap && !new RegExp(`\\b${resolvedCap}\\b`).test(resolvedDisplay)) {
                resolvedDisplay = (resolvedDisplay ? `${resolvedDisplay}, ${resolvedCap}` : resolvedCap);
            }

            // 2. UNIFIED VALIDATION:
            // Trust the backend 'isAddress' flag OR a valid CAP.
            // Do NOT parse address_components manually for 'street_number' here.
            if (!isAddress || !/^\d{5}$/.test(resolvedCap)) {
                showError("Indirizzo incompleto: scegli una via + numero civico dai suggerimenti (es. “Via … 12, Roma”).");
                try {
                    __logAddressAttempt({
                        context: 'home_florame',
                        source: 'cap_modal',
                        status: 'invalid_address',
                        attemptId,
                        query: queryNow,
                        display: String(resolvedDisplay || (input && input.value) || ''),
                        resolvedDisplay: String(resolvedDisplay || ''),
                        formattedAddress,
                        addressComponents,
                        placeId: pid,
                        cap: resolvedCap || '',
                        isAddress: false,
                        reason: 'INVALID_OR_MISSING_CAP'
                    });
                } catch (_) {}
                setLoading(false);
                return;
            }

            // 3. Save Last Address (Unified Style)
            try { 
                localStorage.setItem(ADDR_CACHE_KEY, JSON.stringify({ 
                    display: resolvedDisplay, 
                    postcode: resolvedCap, 
                    placeId: placeId, 
                    ts: Date.now() 
                }));
                try { __addrBookUpsertRecent({ placeId: placeId, display: resolvedDisplay, postcode: String(resolvedCap || '') }); } catch (_) {}
 
            } catch (_) {}

            // 4. Verify Stock
            await verifyStock(resolvedCap, resolvedDisplay, {
                userInitiated: true,
                context: 'home_florame',
                source: 'cap_modal',
                attemptId,
                query: queryNow,
                placeId: pid,
                display: resolvedDisplay,
                resolvedDisplay,
                formattedAddress,
                addressComponents,
                lat: Number.isFinite(Number(place.lat)) ? Number(place.lat) : null,
                lng: Number.isFinite(Number(place.lng)) ? Number(place.lng) : null,
                street: String(place.street || __addressComponentValue(addressComponents, 'route') || '').trim(),
                houseNumber: String(place.houseNumber || __addressComponentValue(addressComponents, 'street_number') || '').trim(),
                city: String(place.city || __addressComponentValue(addressComponents, 'locality') || __addressComponentValue(addressComponents, 'postal_town') || '').trim(),
                province: String(place.province || __addressComponentValue(addressComponents, 'administrative_area_level_2') || '').trim(),
                isAddress: true
            });

        } catch (e) {
            console.error(e);
            try {
                __logAddressAttempt({
                    context: 'home_florame',
                    source: 'cap_modal',
                    status: 'resolve_error',
                    attemptId,
                    query: queryNow,
                    display: String((input && input.value) || ''),
                    placeId: pid,
                    reason: String(e && e.message || e || 'resolve_error')
                });
            } catch (_) {}
            showError("Errore nel recupero dell'indirizzo. Riprova.");
            setLoading(false);
        }
        resetSessionToken(input);
    }

    async function verifyStock(cap, displayAddr, meta = {}) {

        const ctx = String((meta && meta.context) || 'home_florame');
        const userInitiated = !!(meta && meta.userInitiated);
        const placeId = String((meta && (meta.placeId || meta.selectedPlaceId)) || '').trim();
        const query = String((meta && meta.query) || '').trim();
        const attemptId = String((meta && meta.attemptId) || __newAddressAttemptId(ctx)).trim();
        const resolvedDisplay = String((meta && (meta.resolvedDisplay || meta.display)) || displayAddr || '').trim();
        const formattedAddress = String((meta && meta.formattedAddress) || resolvedDisplay || displayAddr || '').trim();
        const addressComponents = Array.isArray(meta && meta.addressComponents) ? meta.addressComponents : [];

        try {

            if (userInitiated) {
                try {
                    __logAddressAttempt({
                        context: ctx,
                        source: String((meta && meta.source) || 'cap'),
                        status: 'start',
                        attemptId,
                        cap: String(cap || ''),
                        display: String(displayAddr || ''),
                        resolvedDisplay,
                        formattedAddress,
                        addressComponents,
                        placeId,
                        query,
                        isAddress: (meta && typeof meta.isAddress === 'boolean') ? meta.isAddress : undefined
                    });
                } catch (_) {}
            }

            const res = await fetch(__stockEndpointUrl(cap, meta));

            let data = null;
            try { data = await res.json(); } catch(_) {}

            if (!res.ok) throw new Error(data?.message || "Errore server");

            if (data && data.covered === false) {
                const assignment = __partnerAssignmentFromResponse(data);
                const uncoveredState = {
                    cap: String(cap || ''),
                    display: String(displayAddr || ''),
                    verified: false,
                    addressVerified: true,
                    covered: false,
                    ts: Date.now(),
                    pairs: [],
                    cameraId: '',
                    partner: data.partner || null,
                     lat: Number.isFinite(Number(meta.lat)) ? Number(meta.lat) : null,
                     lng: Number.isFinite(Number(meta.lng)) ? Number(meta.lng) : null,
                     deliveryLat: Number.isFinite(Number(meta.lat)) ? Number(meta.lat) : null,
                     deliveryLng: Number.isFinite(Number(meta.lng)) ? Number(meta.lng) : null,
                     placeId,
                     street: String(meta.street || '').trim(),
                     houseNumber: String(meta.houseNumber || '').trim(),
                     city: String(meta.city || '').trim(),
                     province: String(meta.province || '').trim(),
                     civicVerified: !!String(meta.houseNumber || '').trim(),
                     ...assignment
                };
                try { localStorage.setItem(STORAGE_KEY, JSON.stringify(uncoveredState)); } catch (_) {}
                try {
                    document.dispatchEvent(new CustomEvent('stock-updated'));
                    document.dispatchEvent(new CustomEvent('cap-check-finish', { detail: uncoveredState }));
                } catch (_) {}
                showError(buildNotServedHtml(data, cap, { address: displayAddr, placeId, context: ctx, query }), { html: true });

                if (userInitiated) {
                    try {
                        __logAddressAttempt({
                            context: ctx,
                            source: String((meta && meta.source) || 'cap'),
                            status: 'not_served',
                            attemptId,
                            cap: String(cap || ''),
                            display: String(displayAddr || ''),
                            resolvedDisplay,
                            formattedAddress,
                            addressComponents,
                            placeId,
                            query,
                            isAddress: (meta && typeof meta.isAddress === 'boolean') ? meta.isAddress : undefined,
                            covered: false,
                            reason: data.errorCode || 'OUT_OF_COVERAGE'
                        });
                    } catch (_) {}
                }

                return;
            }

            if (!data || !data.success) throw new Error(data?.message || "Errore sconosciuto");

            // Success
            const pairs = data.pairs || [];
            const assignment = __partnerAssignmentFromResponse(data);
            const state = { 
                cap: cap, 
                display: displayAddr, 
                verified: true, 
                addressVerified: true,
                covered: data.covered !== false,
                ts: Date.now(), 
                pairs: pairs,
                cameraId: (data && (data.cameraId || data.camera_id) ? String(data.cameraId || data.camera_id) : (Array.isArray(pairs) && pairs[0] ? String((pairs[0].cameraId || pairs[0].camera_id || pairs[0].id || pairs[0].camera || pairs[0].cam || (Array.isArray(pairs[0]) ? pairs[0][0] : '')) || '') : '')),
                partner: data.partner || null,
                 lat: Number.isFinite(Number(meta.lat)) ? Number(meta.lat) : null,
                 lng: Number.isFinite(Number(meta.lng)) ? Number(meta.lng) : null,
                 deliveryLat: Number.isFinite(Number(meta.lat)) ? Number(meta.lat) : null,
                 deliveryLng: Number.isFinite(Number(meta.lng)) ? Number(meta.lng) : null,
                 placeId,
                 street: String(meta.street || '').trim(),
                 houseNumber: String(meta.houseNumber || '').trim(),
                 city: String(meta.city || '').trim(),
                 province: String(meta.province || '').trim(),
                 civicVerified: !!String(meta.houseNumber || '').trim(),
                 ...assignment
            };

            // Persist
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));

            // Global Events
            document.dispatchEvent(new CustomEvent('stock-updated'));
            document.dispatchEvent(new CustomEvent('cap-check-finish', { detail: state }));

            syncUI();

            if (userInitiated) {
                try {
                    __logAddressAttempt({
                        context: ctx,
                        source: String((meta && meta.source) || 'cap'),
                        status: 'success',
                        attemptId,
                        cap: String(cap || ''),
                        display: String(displayAddr || ''),
                        resolvedDisplay,
                        formattedAddress,
                        addressComponents,
                        placeId,
                        query,
                        isAddress: (meta && typeof meta.isAddress === 'boolean') ? meta.isAddress : undefined,
                        covered: true
                    });
                } catch (_) {}
            }

        } catch (e) {

            const msg = e && e.message ? e.message : "Errore connessione.";
            showError(msg);

            if (userInitiated) {
                try {
                    __logAddressAttempt({
                        context: ctx,
                        source: String((meta && meta.source) || 'cap'),
                        status: 'error',
                        attemptId,
                        cap: String(cap || ''),
                        display: String(displayAddr || ''),
                        resolvedDisplay,
                        formattedAddress,
                        addressComponents,
                        placeId,
                        query,
                        isAddress: (meta && typeof meta.isAddress === 'boolean') ? meta.isAddress : undefined,
                        reason: String(msg || 'error')
                    });
                } catch (_) {}
            }

        } finally {
            setLoading(false);
        }
    }


    // --- AUTO RE-VERIFY AFTER RESTORE (Home) ---
    // Se abbiamo un indirizzo/CAP verificato in cache, al reload rilanciamo la verifica
    // per riallineare lo stock "live" senza richiedere click.
    let __autoReverifyDone = false;

    function autoReverifyAfterRestore() {
        try {
            if (__autoReverifyDone) return;
            // evita collisioni con altre verifiche globali (se presenti)
            if (window.__capCheckInProgress) return;

            const st = getCapState();
            if (!st || !st.cap || !st.verified || !st.fresh) return;

            // evita refetch immediato se appena verificato e ricaricato
            const ageMs = Date.now() - Number(st.ts || 0);
            if (ageMs < 15000) { __autoReverifyDone = true; return; }

            let displayTxt = st.display || st.cap;
            let last = null;
            try { last = JSON.parse(localStorage.getItem(ADDR_CACHE_KEY) || 'null'); } catch(_) { last = null; }
            const lastMatchesCap = !!(last && last.display && (!last.postcode || String(last.postcode) === String(st.cap || '')));

            // prefill input + dataset (utile se l'utente fa "Cambia" subito)
            const addrInput = $('capAddressInput');
            if (addrInput) {
                addrInput.value = String(displayTxt || '');
                addrInput.dataset.cap = String(st.cap || '');
                if (lastMatchesCap && last.placeId) {
                    const selected = { placeId: String(last.placeId), display: String(displayTxt || ''), __postcode: String(st.cap || last.postcode || '') };
                    addrInput.dataset.selected = JSON.stringify(selected);
                }
            }

            __autoReverifyDone = true;
            setLoading(true);
            Promise.resolve(verifyStock(String(st.cap), String(displayTxt))).catch(() => {}).finally(() => {});
        } catch (e) {
            console.warn('autoReverifyAfterRestore failed', e);
        }
    }

    // --- UI HELPERS ---
    function setLoading(isLoading) {
        const loader = $('capLoader'); 
        if (loader) loader.style.display = isLoading ? 'flex' : 'none';

        // ✅ HOME: nasconde la barra input durante la verifica disponibilità
        const inputView = $('capViewInput');
        if (inputView) inputView.classList.toggle('cap-is-loading', !!isLoading);
        const addrInput = $('capAddressInput');
        if (addrInput) addrInput.disabled = !!isLoading;
        if(isLoading) showError(null);
    }
    
    
    // ───────────────────────────────────────────────────────────────
    // Friendly CAP/Address error copy + Not-served CTA (unified)
    // ───────────────────────────────────────────────────────────────
    function normalizeCapErrorMsg(msg) {
      try {
        let m = String(msg || '').trim();
        if (!m) return '';
        const ml = m.toLowerCase();

        // Do not touch rich HTML notices
        if (m.includes('<div') || m.includes('<a') || m.includes('<span')) return m;

        // Coverage / not served
        if (ml.includes('non è servita') || ml.includes('non è ancora servita')) return m;

        // Street number / civic number
        if (ml.includes('numero civico') || ml.includes('civico') || ml.includes('street_number') || ml.includes('street number')) {
          return "Non risulta il numero civico indicato per questa via. Controlla il numero oppure scegli un civico vicino (es. “Via … 12, Roma”) dai suggerimenti.";
        }

        // Incomplete / not an address
        if (ml.includes('indirizzo incompleto') || ml.includes('indirizzo completo') || ml.includes('seleziona un indirizzo') || ml.includes('scegli una via') || ml.includes('no_place_id')) {
          return "Indirizzo incompleto: scegli una via + numero civico dai suggerimenti (es. “Via … 12, Roma”).";
        }

        // Timeout / slow
        if (ml.includes('timeout') || (ml.includes('riprova') && ml.includes('secondo')) || ml.includes('ci stiamo mettendo')) {
          return "Ci stiamo mettendo un attimo… riprova tra qualche secondo.";
        }

        // Connection
        if (ml.includes('connessione') || ml.includes('network') || ml.includes('failed to fetch')) {
          return "Connessione instabile. Riprova tra qualche secondo.";
        }

        // Generic server
        if (ml.includes('errore server') || ml.startsWith('http ')) {
          return "Ops, c’è stato un problema tecnico. Riprova tra poco.";
        }

        return m;
      } catch (_) {
        return String(msg || '');
      }
    }

    
    function escapeHtml(str) {
      return String(str || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    }

function buildNotServedHtml(data, cap, meta) {
      const msg = (data && data.message) ? String(data.message) : "Ci dispiace 💔: la zona selezionata non è servita dal servizio.";
      const details = (data && data.details) ? String(data.details) : "Vuoi che ti avvisiamo appena iniziamo a consegnare lì? Ci aiuti tantissimo a scegliere le prossime zone da raggiungere.";
      const address = meta && meta.address ? String(meta.address) : "";
      const placeId = meta && meta.placeId ? String(meta.placeId) : "";
      const context = meta && meta.context ? String(meta.context) : "cap";
      const prefillEmail = (() => {
        try {
          const direct = (window.AWI_CUSTOMER_EMAIL || window.__AWI_CUSTOMER_EMAIL || window.customerEmail || '') || '';
          if (direct && /.+@.+\..+/.test(String(direct))) return String(direct).trim();
        } catch (_) {}
        try {
          const ls = localStorage.getItem('florame_customer_email') || '';
          if (ls && /.+@.+\..+/.test(String(ls))) return String(ls).trim();
        } catch (_) {}
        return '';
      })();

      const privacyUrl = (CONFIG && CONFIG.privacyUrl) ? String(CONFIG.privacyUrl) : "/policies/privacy-policy";

      return `
        <div class="cap-not-served" data-cap="${escapeHtml(cap || '')}" data-address="${escapeHtml(address)}" data-place-id="${escapeHtml(placeId)}" data-context="${escapeHtml(context)}">
          <div class="cap-not-served-title">${escapeHtml(msg)}</div>
          <div class="cap-not-served-body">${escapeHtml(details)}</div>

          <form class="cap-wl-form" novalidate>
            <input class="cap-wl-input" type="email" inputmode="email" autocomplete="email"
              placeholder="Inserisci la tua email" value="${escapeHtml(prefillEmail)}" />
            <button class="cap-wl-btn" type="submit">Invia</button>
          </form>

          <div class="cap-wl-msg" aria-live="polite"></div>

          <div class="cap-not-served-foot">Ci aiuti a scegliere le prossime zone: è una metrica fondamentale per la nostra espansione ✨</div>
          <a class="cap-wl-privacy" href="${escapeHtml(privacyUrl)}" target="_blank" rel="noopener">Privacy</a>
        </div>
      `;
    }

    // ───────────────────────────────────────────────────────────────
    // 📩 Coverage Waitlist (CAP non servito) — raccolta email
    // ───────────────────────────────────────────────────────────────
    const __COV_WL_SENT_PREFIX = 'florame_covwl_sent_v1::';
    const __COV_WL_SESSION_KEY = 'florame_covwl_session_v1';

    function __covWlGetEndpoint() {
      return (CONFIG && CONFIG.coverageWaitlistEndpoint)
        ? String(CONFIG.coverageWaitlistEndpoint)
        : "https://us-central1-flower-ai-2d7aa.cloudfunctions.net/collectCoverageWaitlist";
    }

    function __covWlGetUtm() {
      try {
        const sp = new URLSearchParams(window.location.search || '');
        const keys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content'];
        const out = {};
        keys.forEach(k => { const v = sp.get(k); if (v) out[k] = v; });
        return Object.keys(out).length ? out : null;
      } catch (_) { return null; }
    }

    function __covWlGetSessionId() {
      try {
        const existing = sessionStorage.getItem(__COV_WL_SESSION_KEY);
        if (existing) return existing;
      } catch (_) {}
      const sid = (Math.random().toString(16).slice(2) + Date.now().toString(16)).slice(0, 32);
      try { sessionStorage.setItem(__COV_WL_SESSION_KEY, sid); } catch (_) {}
      return sid;
    }

    function __covWlIsValidEmail(email) {
      const e = String(email || '').trim();
      return /.+@.+\..+/.test(e);
    }

    function __covWlAlreadySent(cap) {
      try { return !!localStorage.getItem(__COV_WL_SENT_PREFIX + String(cap || '')); } catch (_) { return false; }
    }

    function __covWlMarkSent(cap) {
      try { localStorage.setItem(__COV_WL_SENT_PREFIX + String(cap || ''), String(Date.now())); } catch (_) {}
    }

    function __ensureCoverageWaitlistHandlers() {
      if (window.__florameCoverageWaitlistBound) return;
      window.__florameCoverageWaitlistBound = true;

      document.addEventListener('submit', async (e) => {
        const form = e.target && e.target.closest ? e.target.closest('.cap-wl-form') : null;
        if (!form) return;

        e.preventDefault();

        const wrap = form.closest('.cap-not-served');
        const input = form.querySelector('.cap-wl-input');
        const btn = form.querySelector('.cap-wl-btn');
        const msgEl = wrap ? wrap.querySelector('.cap-wl-msg') : null;

        const cap = wrap ? String(wrap.dataset.cap || '').trim() : '';
        const address = wrap ? String(wrap.dataset.address || '').trim() : '';
        const placeId = wrap ? String(wrap.dataset.placeId || '').trim() : '';
        const context = wrap ? String(wrap.dataset.context || 'cap').trim() : 'cap';

        const email = input ? String(input.value || '').trim().toLowerCase() : '';

        const setMsg = (t, ok) => {
          if (!msgEl) return;
          msgEl.textContent = t || '';
          msgEl.classList.toggle('ok', !!ok);
          msgEl.classList.toggle('err', !ok);
        };

        if (!__covWlIsValidEmail(email)) {
          setMsg('Inserisci un’email valida.', false);
          input && input.focus && input.focus();
          return;
        }

        if (cap && __covWlAlreadySent(cap)) {
          setMsg('Grazie! Ti abbiamo già inserito in lista per questo CAP.', true);
          return;
        }

        try {
          setMsg('Invio…', true);
          if (btn) btn.disabled = true;

          const payload = {
            email,
            cap: cap || '',
            address: address || '',
            placeId: placeId || '',
            context,
            sessionId: __covWlGetSessionId(),
            page: window.location.href,
            referrer: document.referrer || '',
            locale: navigator.language || '',
            tz: (Intl && Intl.DateTimeFormat) ? (Intl.DateTimeFormat().resolvedOptions().timeZone || '') : '',
            ua: navigator.userAgent || '',
            utm: __covWlGetUtm()
          };

          const resp = await fetch(__covWlGetEndpoint(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true
          });

          let out = null;
          try { out = await resp.json(); } catch (_) {}

          if (!resp.ok || !out || !out.ok) {
            throw new Error((out && out.message) ? out.message : 'Errore invio');
          }

          __covWlMarkSent(cap || '');
          try { localStorage.setItem('florame_customer_email', email); } catch (_) {}
          try {
            __logAddressAttempt({
              source: 'coverage_waitlist',
              context,
              status: 'not_served',
              covered: false,
              query: address || cap || '',
              display: address || '',
              resolvedDisplay: address || '',
              resolvedCap: cap || '',
              placeId,
              customerEmail: email,
              marketingConsent: false
            });
          } catch (_) {}

          try {
            if (window.dataLayer && typeof window.dataLayer.push === 'function') {
              window.dataLayer.push({ event: 'coverage_waitlist_submit', cap: cap || '', context });
            }
          } catch (_) {}

          setMsg('Grazie! Ti avviseremo appena consegniamo in zona 💌', true);
          if (input) input.disabled = true;
          if (btn) { btn.disabled = true; btn.textContent = 'Inviato'; }
        } catch (err) {
          setMsg('Non siamo riusciti a registrare la richiesta. Riprova tra poco.', false);
          if (btn) btn.disabled = false;
        }
      }, true);
    }

    // Bind handlers subito (delegato, non pesa)
    __ensureCoverageWaitlistHandlers();
function showError(msg, opts = {}) {
        const box = $('capMsg');
        if (!box) return;

        if (!msg) {
          box.style.display = 'none';
          box.textContent = '';
          return;
        }

        const useHtml = !!opts.html;
        const safeMsg = useHtml ? String(msg) : normalizeCapErrorMsg(msg);

        if (useHtml) box.innerHTML = safeMsg;
        else box.textContent = safeMsg;

        box.style.display = 'block';
    }

    function syncUI() {
        const state = getCapState();
        const inputView = $('capViewInput');
        if (inputView) inputView.style.display = 'block';
        
        if (state.verified && state.fresh) {
            // Header e card consumano lo stesso display canonico, già validato
            // contro il CAP salvato da FlorameHomeAddress.readState().
            let displayTxt = state.display || state.cap;
            let last = null;
            try { last = JSON.parse(localStorage.getItem(ADDR_CACHE_KEY) || 'null'); } catch(_) { last = null; }

            // ✅ Ripristina anche il valore input + dataset (utile per auto-verifica e per "Cambia")
            const addrInput = $('capAddressInput');
            if (addrInput) {
                addrInput.value = String(displayTxt || '');
                addrInput.dataset.cap = String(state.cap || '');
                if (last && last.placeId) {
                    const selected = { placeId: String(last.placeId), display: String(displayTxt || ''), __postcode: String(state.cap || last.postcode || '') };
                    addrInput.dataset.selected = JSON.stringify(selected);
                }
            }

            try {
              if (window.FlorameHomeAddress && typeof window.FlorameHomeAddress.updateSummary === 'function') {
                window.FlorameHomeAddress.updateSummary(displayTxt, true);
              }
              const addressSection = document.querySelector('[data-home-address-section]');
              if (addressSection) addressSection.removeAttribute('data-address-requested');
              if (window.FlorameHomeAddress && typeof window.FlorameHomeAddress.close === 'function') {
                window.FlorameHomeAddress.close({ restoreFocus: false });
              }
            } catch (_) {}
        } else {
            try {
              if (window.FlorameHomeAddress && typeof window.FlorameHomeAddress.updateSummary === 'function') {
                window.FlorameHomeAddress.updateSummary('', false);
              }
            } catch (_) {}
        }
    }

    function resetCapState() {
        localStorage.removeItem(STORAGE_KEY);
        document.dispatchEvent(new CustomEvent('stock-updated'));
        syncUI();
        if ($('capAddressInput')) $('capAddressInput').value = '';
        try {
          if (window.FlorameHomeAddress && typeof window.FlorameHomeAddress.open === 'function') {
            window.FlorameHomeAddress.open({ source: 'change', scroll: false, focus: true });
          }
        } catch (_) {}
    }

    // --- INIT ---
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initInlineModule);
    } else {
        initInlineModule();
    }
})();


// --- FIX: Helper mancante ---
function __cleanAddress(str) { 
  // Rimuove spazi extra e normalizza
  return String(str || '').trim(); 
}

// --- FIX: Best Match più robusto ---
function __addrBookBestMatch(typed) {
  const raw = String(typed || '').trim();
  if (!raw) return null;

  // Usa cleanAddress e poi normalizza per il confronto
  const t = __addrKeyLoose(__cleanAddress(raw));
  const tCompactLen = t.replace(/\s/g, '').length;
  if (!t) return null;

  const { saved, recent } = __addrBookSections();

  const scoreOf = (entry) => {
    const cand = __addrKeyLoose(__cleanAddress(String(entry?.display || '')));
    if (!cand) return 0;

    // 1. Corrispondenza Esatta
    if (cand === t) return 100;

    // 2. Prefisso forte (Es. Tu scrivi "Via Ro", salvato è "Via Roma")
    // Questo è fondamentale mentre si digita
    if (cand.startsWith(t)) return 95; 

    // 3. Inverso (Es. Tu scrivi "Via Roma 10", salvato è "Via Roma")
    if (t.startsWith(cand) && cand.length > 5) return 90;

    // Richiede almeno 3 caratteri digitati per fuzzy/contains
    if (tCompactLen < 3) return 0;

    // 4. Contiene la stringa (Es. Scrivi "Roma", salvato "Via Roma")
    if (cand.includes(t)) return 85;

    // 5. Token Overlap (parole sparse)
    const toks = t.split(' ').filter(Boolean).filter(x => x.length >= 2);
    if (!toks.length) return 0;

    let hit = 0;
    for (const tok of toks) {
      if (cand.includes(tok)) hit++;
    }
    const ratio = hit / toks.length;

    if (ratio === 1) return 80; // Tutti i token presenti
    if (ratio >= 0.75) return 70; // Buona parte

    return 0;
  };

  let best = null;
  let bestScope = '';
  let bestScore = 0;

  const scan = (arr, scope) => {
    for (const e of (arr || [])) {
      const sc = scoreOf(e);
      // Priorità ai salvati rispetto ai recenti a parità di score
      if (sc > bestScore || (sc === bestScore && bestScope !== 'saved' && scope === 'saved')) {
        best = e; bestScope = scope; bestScore = sc;
      }
    }
  };

  scan(saved, 'saved');
  scan(recent, 'recent');

  if (!best || !best?.placeId) return null;

  // Soglia minima per mostrare il risultato (65 permette risultati parziali buoni)
  if (bestScore < 65) return null;

  return { entry: best, scope: bestScope, score: bestScore };
}
